import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { canCreateInterestGroup } from "@/helpers/permissions";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { BuildingForm } from "@/modules/buildings/components/BuildingForm";
import { buildingMutations } from "@/queries/buildings/mutations";
import { useBuildingQueries } from "@/queries/buildings/queryOptions";
import { routes } from "@/routes";

export function CreateBuildingPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const hasPermission = usePermission();
  const buildingQueries = useBuildingQueries();
  const buildingsQuery = useQuery(buildingQueries.list());
  const createBuilding = buildingMutations.useAddBuilding();

  if (buildingsQuery.error) {
    return <ErrorPage error={buildingsQuery.error} />;
  }

  if (buildingsQuery.isFetching) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <BuildingForm
      isEditMode={false}
      defaultValues={{ shouldCreateRealEstateGroup: hasPermission(canCreateInterestGroup) }}
      isSubmitting={createBuilding.isPending}
      existingBuildings={buildingsQuery.data?.items ?? []}
      onSubmit={(values) => {
        createBuilding.mutate(
          { name: values.name, shouldCreateRealEstateGroup: values.shouldCreateRealEstateGroup === true },
          { onSuccess: () => void navigate(routes.buildings.list({ slug })) },
        );
      }}
    />
  );
}
